import React, { useCallback, useMemo } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import styled, { css, keyframes } from 'styled-components';

import { hasOrbitAnswer } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitThemeVariant } from '../OrbitThemeScope';
import { TOrbit } from '../i18n/TModules';
import { getOrbitOutcomeCopy } from '../outcomeCopy';
import { OrbitBodyText, orbitColorsFor, orbitRole, orbitTokens, orbitType } from '../styles';
import OrbitSupportBlocks, {
  drawsSupportPanel,
  knownSupportBlocks
} from '../support/components/OrbitSupportBlocks';
import { RESPONSE_BLOCK_TYPES } from '../support/supportFormat';
import { OrbitState, OrbitTurn } from '../types';
import OrbitAnswer, { drawsAnalysisCard } from './OrbitAnswer';
import OrbitFollowUps from './OrbitFollowUps';
import OrbitMarkReply from './OrbitMarkReply';
import OrbitMetricResults from './OrbitMetricResults';

/**
 * A turn still waiting for its `id` (POST in flight or first poll pending) has no server record
 * yet, so the card takes the question and an explicit state instead of a full `OrbitTurn`.
 */
export interface IOrbitTurnCardProps {
  question: string;
  state: OrbitState;
  turn?: OrbitTurn;
  /**
   * This is the newest turn in the thread, so its assistant column is a live region: an answer that
   * arrives seconds after the question is announced once, and re-entering a conversation does not
   * replay one announcement per historical turn.
   */
  announce?: boolean;
  /**
   * Retry handler; it takes the question so the parent can memoise ONE callback for every card.
   * `React.memo` below is pointless against per-row inline arrows.
   */
  onRetry?: (question: string) => void;
  retryDisabled?: boolean;
  /** Draws div.msgTag `4952:7133`: this question continued the investigation, context unchanged. */
  continuation?: boolean;
  /** Draws the follow-up chip row under this turn's meta line. Only the newest answer gets it. */
  showFollowUps?: boolean;
  /**
   * `page` draws the full-page thread (6421:9962): a gradient question bubble with no meta line,
   * and replies set beside a bare mark with no bubble or meta line. `panel` draws the side panel's
   * (6034:4796): the same gradient bubble and bare mark at panel scale, replies in a bubble.
   */
  variant?: OrbitTurnCardVariant;
}

export type OrbitTurnCardVariant = 'panel' | 'page';

type ProgressStep = 'authorizing' | 'resolving' | 'retrieving';
type StepStatus = 'done' | 'active' | 'pending';

const PROGRESS_STEPS: ProgressStep[] = ['authorizing', 'resolving', 'retrieving'];

/** `5017:7866` / `5017:7872`: the ticked step's own text glyph, 9 x 12 at Inter Regular 10. */
const STEP_DONE_GLYPH = '✓';

// A step says what it did only once it is ticked; the row still running is title-only in the frame
// (`4952:6695`). `retrieving` is never `done` under ACTIVE_STEP_INDEX below, so it carries no
// `detail` string at all and asking i18n for one would render the raw key path.
const STEPS_WITH_DETAIL: ReadonlySet<ProgressStep> = new Set<ProgressStep>([
  'authorizing',
  'resolving'
]);

// `queued` = accepted, nothing verified yet; `processing` = the worker is past governance and
// running the query, which the drawing shows as two ticks and a spinner on the last row.
const ACTIVE_STEP_INDEX: Record<'queued' | 'processing', number> = {
  queued: 0,
  processing: 2
};

const stepStatus = (state: OrbitState, index: number): StepStatus => {
  const activeIndex =
    state === 'processing' ? ACTIVE_STEP_INDEX.processing : ACTIVE_STEP_INDEX.queued;

  if (index < activeIndex) {
    return 'done';
  }
  return index === activeIndex ? 'active' : 'pending';
};

const formatTurnTime = (createdAt: string | undefined): string | null => {
  if (createdAt === undefined) {
    return null;
  }
  const date = parseISO(createdAt);
  return isValid(date) ? format(date, 'h:mm a') : null;
};

// Panel thread geometry from div#chat 6034:4796: the reply sits 12 under its question (6034:4812
// ends at y 221, 6034:4815 starts at 233), with the bare mark 8 left of it.
// On the page the reply sits 18 under its question in five of the newer frames' measurements
// (6424:11847 → 6424:11849 and 6424:11906 → 6424:11908, 6265:4564 → 6265:4566, 6265:4668 →
// 6265:4675 at 17), and the analysing card 28 (6459:16110 → 6459:16112). 6421:9962's 0, the value
// this carried, left a reply that opens on a panel touching the bubble.
const PANEL_REPLY_GAP = 12;
const PAGE_REPLY_GAP = 18;
const PAGE_PROGRESS_GAP = 28;

const Root = styled.article<{ $gap: number }>`
  && > * + * {
    margin-top: ${({ $gap }) => $gap}px;
  }
`;

const MessageRow = styled.div<{ $align: 'start' | 'end' }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ $align }) => ($align === 'end' ? 'flex-end' : 'flex-start')};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 8px;
  }
`;

/* Both panel bubbles run the reply column beside the Orbit mark: the thread body is 467.5 wide
   (`styles.ts` works that out from the 500 panel) and the mark with its gutter takes 22 of it.
   The design calls the column 447 +/- 2, which 445.5 meets; the drawn 429.6 of 6034:4813 and
   6034:4819 did not, and left prose replies narrower than the response blocks' own 445 cap
   (`support/response/responseScale.ts`). */
const MessageBody = styled.div<{ $full?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: ${({ $full }) => ($full ? '1 1 auto' : '0 1 auto')};
  min-width: 0;
  max-width: ${({ $full }) => ($full ? 'none' : '445.5px')};

  > * + * {
    margin-top: 4px;
  }
`;

// 6265:4213 hugs its text up to a 676.8 max width.
const PageQuestionBody = styled(MessageBody)`
  max-width: 676.8px;
`;

// The reply column is 845 wide and starts 25 right of the mark's own left edge (6265:4219 at x 523,
// the mark at 498), and the mark sits 1 inside the 1068 column (x 497).
const PageReplyBody = styled(MessageBody)`
  max-width: 845px;

  && {
    margin-left: 11px;
  }
`;

// Page response blocks cap themselves (`ResponseReply $maxWidth`: 888, 845 or 960 by type).
const PageResponseBody = styled(PageReplyBody)`
  max-width: none;
`;

// 6421:10232: 14 x 15, its top 4 below the 23px intro line's (y 151 against 147).
const PageMark = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  width: 14px;
  height: 15px;
  margin: 4px 0 0 1px;
`;

// 6051:6152: the bare 14 x 15 mark, top-aligned with the reply. No user avatar is drawn.
const PanelMark = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  width: 14px;
  height: 15px;
`;

/*
  6034:4813: radii 14 14 4 14 (TL TR BR BL), pad 11 / 13, Inter Regular 13 in white on the page's
  gradient. White on the gradient's light #268AFF end is about 3.4:1 (4.9:1 on #1B6FD1), under
  WCAG AA's 4.5:1 for text this size; the design's own reading, as on the page.
*/
const UserBubble = styled.p`
  margin: 0;
  padding: 11px 13px;
  border-radius: 14px 14px 4px 14px;
  background-image: linear-gradient(135deg, #268aff 0%, #1b6fd1 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 400;
  line-height: 19.5px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

// 6265:4213: radii 14 14 5 14 (TL TR BR BL), the gradient's in-tab stops, and its 18% glow.
const PageUserBubble = styled(UserBubble)`
  padding: 13px 18px;
  border-radius: 14px 14px 5px 14px;
  box-shadow: 0 10px 26px rgba(38, 138, 255, 0.18);
  font-size: 14.5px;
  line-height: 22px;
`;

// 6034:4819: #101830 under a 1px #212D4B rule, radii 14 14 14 4, pad 11 / 13, Regular 13 #AEBBD0.
const AssistantBubble = styled.div`
  padding: 11px 13px;
  border: 1px solid ${orbitTokens.border};
  border-radius: 14px 14px 14px 4px;
  background: ${orbitTokens.surfaceAlt};
  color: ${orbitTokens.textSecondary};
  font-size: 13px;
  font-weight: 400;
  line-height: 19.5px;
`;

// div.say 6265:4220: the page sets a prose reply as plain text.
const PageReplyText = styled.div`
  color: ${orbitTokens.textSecondary};
  font-size: 14.5px;
  font-weight: 400;
  line-height: 23px;
`;

// Support replies in the 6034:* frames: a #101830 bubble, not the analytics bubble.
const SupportBubble = styled.div`
  padding: 11px 13px;
  border: 1px solid ${orbitTokens.border};
  border-radius: 14px 14px 14px 4px;
  background: ${orbitTokens.surfaceAlt};
`;

// An answered turn draws `div.analysis-card` (figma-spec-v2.md `4952:8087`) in place of the chat
// bubble. The card and every element under it set their own type, so this shell carries none.
const AnswerSlot = styled.div``;

// TODO(AF-11): 7.8 measured, taken to the nearest clean step; the weakest inference in § 4b.
const MessageTag = styled.span`
  display: block;
  padding: 0 2px 1px;
  color: ${orbitTokens.threadFaint};
  ${orbitType.helping}
  font-weight: 400;
  text-align: right;
`;

const Timestamp = styled.time`
  align-self: flex-end;
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
`;

// `5017:9863` is Inter Regular 10.5, the same step the user-side Timestamp above already takes; the
// assistant line was half a step under it (browser-audit-parity-20260913.md:141). PLA-9563 § 7b.
const AssistantMeta = styled.time`
  display: block;
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
`;

/*
  Processing card. The unscaled `4531:334` (element-geometry-processing.md) is the source: its type
  is all on the clean ramp - 12, 11, 10.5, 10 - so unlike the thread panels of § 4b its numbers are
  buildable as drawn.

  The three blocks carry their own top margins rather than a `> * + *` rule on this wrapper, because
  the two they would apply to both reset `margin: 0` in their own styled rule; at equal specificity
  the later declaration won and BOTH gaps rendered as 0 (measured on :3005, heading ends 455 and the
  step list starts 455). The drawn gaps are also not equal: heading ends 248.8 and the list starts
  264, the list ends 383.8 and the console starts 399.8. PLA-9563 § 7c.
*/
// 6458:15726 puts the page's steps at col+46, 20 right of the reply column.
const Progress = styled.div<{ $page: boolean }>`
  ${({ $page }) =>
    $page &&
    css`
      margin-left: 20px;
    `}
`;

/*
  `5017:7830` draws the ORBIT MARK before the heading, not a sparkle: `5199:9958` is a 14 x 15
  frame of five `#D2704C` vectors whose child geometry is identical to `5199:10088`, the node
  `OrbitMarkReply` already ships — so the heading reuses that component instead of a third copy.

  It is laid out on the step rows' own rhythm rather than a gap of its own. The frame puts the
  mark at x 30 and the heading text at x 56, and the step icons below occupy an 18 box at the
  body's 28 with a 10 gap to their text; centring the 14 mark in that same 18 box lands it at
  exactly 30, so both columns line up by construction.
*/
// The page heading is 6459:16112: Semi Bold 12 in white.
const ProgressHeading = styled.p<{ $page: boolean }>`
  display: flex;
  align-items: flex-start;
  margin: 0;
  color: ${({ $page }) => ($page ? '#FFFFFF' : orbitTokens.action)};
  ${({ $page }) => ($page ? orbitRole('body3') : orbitType.containerTitle)}
  font-weight: ${({ $page }) => ($page ? 600 : 500)};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 10px;
  }
`;

const HeadingMark = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  justify-content: center;
  width: 18px;
`;

const StepList = styled.ol`
  margin: 15.2px 0 0;
  padding: 0;
  list-style: none;

  > * + * {
    margin-top: 14px;
  }
`;

const StepRow = styled.li`
  display: flex;
  align-items: flex-start;

  > * + * {
    margin-left: 10px;
  }
`;

const StepText = styled.div`
  min-width: 0;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/*
  `5017:7865` is an 18 box at radius 9 holding the TEXT glyph `✓` at Inter 10 over a 12px line
  (`5017:7866`), not an icon-font outline: the antd `CheckOutlined` that stood here drew a
  different tick at a different weight, and Figma exposes no outline for a text glyph.

  The ticked box is FILLED, never stroked — the frame gives it `orbitTokens.positive` at 14% paint
  opacity and no stroke at all, written out as rgba here because jsdom's CSS parser drops 8-digit
  hex and the fill would then be untestable. The 1.6 ring it carried made every ticked row read as
  another spinner, which is the mismatch the user reported.
*/
const StepIcon = styled.span<{ $status: StepStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  ${({ $status }) => {
    if ($status === 'done') {
      return css`
        background-color: rgba(30, 185, 128, 0.14);
        color: ${orbitTokens.positive};
      `;
    }
    if ($status === 'active') {
      return css`
        border: 1.6px solid ${orbitTokens.action};
        border-bottom-color: transparent;
        animation: ${spin} 0.9s linear infinite;
      `;
    }
    return css`
      border: 1.6px solid ${orbitTokens.border};
    `;
  }}
`;

const StepLabel = styled.span<{ $status: StepStatus; $page: boolean }>`
  display: block;
  color: ${({ $status }) => {
    if ($status === 'active') {
      return orbitTokens.action;
    }
    return $status === 'done' ? orbitTokens.text : orbitTokens.textMuted;
  }};
  ${orbitType.containerBody}
  font-weight: ${({ $page }) => ($page ? 600 : 500)};
`;

const StepDetail = styled.span`
  display: block;
  margin-top: 1px;
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
`;

// `4531:379` is 38.4 tall over a 13px line, and its text starts at x 36.4 against the box's own
// 21.6 - a 14.8 inset, 14 of it inside the stroke, not the 19 this carried. PLA-9563 § 7c.
// The page's box (6459:16134) is as wide as the step list, 436.5, with its text 19 in.
const Console = styled.p<{ $page: boolean }>`
  margin: 16px 0 0;
  padding: ${({ $page }) => ($page ? '11.9px 18px' : '11.9px 14px')};
  border: 1px solid ${orbitTokens.border};
  border-radius: 8px;
  background: ${orbitTokens.surfaceConsole};
  color: ${orbitTokens.action};
  ${orbitType.helping}
  ${({ $page }) =>
    $page &&
    css`
      max-width: 436.5px;
    `}
`;

// TODO(AF-10): no in-scope frame draws the error / timeout card; label + text-button until then.
const OutcomeCard = styled.div`
  > * + * {
    margin-top: 8px;
  }
`;

const OutcomeText = styled(OrbitBodyText)<{ $negative: boolean }>`
  display: flex;
  align-items: flex-start;
  color: ${({ $negative }) => ($negative ? orbitTokens.negative : orbitTokens.textSecondary)};

  > * + * {
    margin-left: 6px;
  }

  .anticon {
    margin-top: 2px;
  }
`;

// PLA-9581's second line. Indented past the label's 13px icon and its 6px gap so the explanation
// hangs under the label text rather than under the icon.
const OutcomeBody = styled(OrbitBodyText)`
  padding-left: 19px;
  color: ${orbitTokens.textSecondary};
`;

const RetryButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  color: ${orbitTokens.action};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 500;

  &:hover:not(:disabled),
  &:focus-visible {
    text-decoration: underline;
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const RESPONSE_TYPES: ReadonlySet<string> = new Set(RESPONSE_BLOCK_TYPES);

const isRetryable = (turn: OrbitTurn): boolean =>
  turn.state === 'failed' || turn.state === 'timeout' || turn.outcome === 'error';

const OrbitTurnCard: React.FC<IOrbitTurnCardProps> = ({
  question,
  state,
  turn,
  announce = false,
  onRetry,
  retryDisabled = false,
  continuation = false,
  showFollowUps = false,
  variant = 'panel'
}) => {
  const { t } = useMultiLanguageTranslation();
  const themeVariant = useOrbitThemeVariant();
  const avatarMarkFill = orbitColorsFor(themeVariant).avatarAssistantMark;
  const page = variant === 'page';
  const createdAt = turn?.created_at;
  // The page draws neither meta line.
  const time = page ? null : formatTurnTime(createdAt);
  const terminal = state === 'completed' || state === 'failed' || state === 'timeout';

  const handleRetry = useCallback(() => onRetry?.(question), [onRetry, question]);

  // Built here rather than passed in: the card already holds the whole turn, and an element built
  // by the parent is a new identity on every render, which defeats the `React.memo` below.
  const chart = useMemo(
    () =>
      hasOrbitAnswer(turn) && turn.metric_results.length > 0 ? (
        <OrbitMetricResults
          metricResults={turn.metric_results}
          visualization={turn.answer.visualization}
        />
      ) : undefined,
    [turn]
  );
  const supportBlocks = useMemo(() => knownSupportBlocks(turn?.blocks), [turn]);

  const renderAssistantBody = (): React.ReactNode => {
    if (!terminal) {
      return (
        <Progress $page={page} data-testid="orbit-turn-progress">
          <ProgressHeading $page={page}>
            <HeadingMark aria-hidden="true">
              <OrbitMarkReply />
            </HeadingMark>
            <span>{t(`${TOrbit}.progress.analysing`)}</span>
          </ProgressHeading>
          <StepList>
            {PROGRESS_STEPS.map((step, index) => {
              const status = stepStatus(state, index);
              return (
                <StepRow key={step} data-testid={`orbit-turn-step-${step}`} data-status={status}>
                  <StepIcon $status={status} aria-hidden="true">
                    {status === 'done' ? STEP_DONE_GLYPH : null}
                  </StepIcon>
                  <StepText>
                    <StepLabel
                      $status={status}
                      $page={page}
                      data-testid={`orbit-turn-step-${step}-title`}
                    >
                      {t(`${TOrbit}.progress.${step}.title`)}
                    </StepLabel>
                    {status === 'done' && STEPS_WITH_DETAIL.has(step) ? (
                      <StepDetail data-testid={`orbit-turn-step-${step}-detail`}>
                        {t(`${TOrbit}.progress.${step}.detail`)}
                      </StepDetail>
                    ) : null}
                  </StepText>
                </StepRow>
              );
            })}
          </StepList>
          <Console $page={page} data-testid="orbit-turn-console">
            {t(`${TOrbit}.progress.console`)}
          </Console>
        </Progress>
      );
    }

    if (turn === undefined) {
      return null;
    }

    if (supportBlocks.length > 0) {
      // The page keeps the blocks' own default scale; only the panel asks for its smaller one.
      return page ? (
        <OrbitSupportBlocks blocks={supportBlocks} />
      ) : (
        <OrbitSupportBlocks blocks={supportBlocks} variant="panel" />
      );
    }

    if (hasOrbitAnswer(turn)) {
      return <OrbitAnswer turn={turn} chart={chart} />;
    }

    const outcomeCopy = getOrbitOutcomeCopy(turn);
    if (outcomeCopy === null) {
      return null;
    }

    return (
      <OutcomeCard data-testid="orbit-turn-outcome">
        <OutcomeText $negative={outcomeCopy.tone === 'negative'}>
          <outcomeCopy.Icon aria-hidden="true" />
          <span>{t(outcomeCopy.labelKey)}</span>
        </OutcomeText>
        {outcomeCopy.bodyKey !== undefined ? (
          <OutcomeBody data-testid="orbit-turn-outcome-body">{t(outcomeCopy.bodyKey)}</OutcomeBody>
        ) : null}
        {onRetry !== undefined && isRetryable(turn) ? (
          <RetryButton
            type="button"
            disabled={retryDisabled}
            data-testid="orbit-turn-retry"
            onClick={handleRetry}
          >
            {t(`${TOrbit}.actions.retry`)}
          </RetryButton>
        ) : null}
      </OutcomeCard>
    );
  };

  const assistantBody = renderAssistantBody();
  // The analysis card is the container for a governed RESULT. A prose reply (clarify, unsupported,
  // insufficient data, an error) is a chat message and takes the assistant bubble, mark included.
  const answered = turn !== undefined && drawsAnalysisCard(turn);
  const isSupport = supportBlocks.length > 0;
  const supportShell = drawsSupportPanel(supportBlocks) ? AnswerSlot : SupportBubble;
  const proseShell = page ? PageReplyText : AssistantBubble;
  const AssistantShell = isSupport ? supportShell : answered ? AnswerSlot : proseShell;
  const QuestionBody = page ? PageQuestionBody : MessageBody;
  let blockGap = PANEL_REPLY_GAP;
  if (page) {
    blockGap = terminal ? PAGE_REPLY_GAP : PAGE_PROGRESS_GAP;
  }
  const selfCapped = isSupport && supportBlocks.every(block => RESPONSE_TYPES.has(block.type));
  let ReplyBody = MessageBody;
  if (page) {
    ReplyBody = selfCapped ? PageResponseBody : PageReplyBody;
  }

  let replyMark: React.ReactNode = null;
  if (page) {
    // The page keeps the mark's column for every reply so the content holds its x; the progress
    // card brings a mark of its own.
    replyMark = (
      <PageMark aria-hidden="true" data-testid="orbit-turn-mark">
        {terminal ? <OrbitMarkReply fill={avatarMarkFill} /> : null}
      </PageMark>
    );
  } else if (terminal && !answered) {
    replyMark = (
      <PanelMark aria-hidden="true" data-testid="orbit-turn-mark">
        <OrbitMarkReply fill={avatarMarkFill} />
      </PanelMark>
    );
  }

  return (
    <Root data-testid="orbit-turn-card" data-state={state} $gap={blockGap}>
      {question === '' ? null : (
        <MessageRow $align="end">
          <QuestionBody>
            {page ? (
              <PageUserBubble data-testid="orbit-turn-question">{question}</PageUserBubble>
            ) : (
              <UserBubble data-testid="orbit-turn-question">{question}</UserBubble>
            )}
            {continuation ? (
              <MessageTag data-testid="orbit-turn-continuation">
                {t(`${TOrbit}.thread.continuation`)}
              </MessageTag>
            ) : null}
            {time !== null ? (
              <Timestamp dateTime={createdAt} data-testid="orbit-turn-time">
                {t(`${TOrbit}.thread.meta`, { who: t(`${TOrbit}.thread.you`), time })}
              </Timestamp>
            ) : null}
          </QuestionBody>
        </MessageRow>
      )}
      {assistantBody !== null ? (
        <MessageRow $align="start">
          {/*
            In the panel only a reply in a bubble or a support panel is drawn with the mark beside
            it (6051:6152). The analysis card spans the body at the frame's own inset (`5032:10873`,
            with no mark gutter) and so does the processing card (`4531:354` and its step list both
            start at the body's 21.6).
          */}
          {replyMark}
          {/*
            The live region is the assistant COLUMN of the newest turn, not the progress card: the
            progress card unmounts at the exact moment the answer replaces it, so a live region
            inside it can never announce the answer it was waiting for.
          */}
          <ReplyBody
            $full={page || !terminal || answered || isSupport}
            role={announce ? 'status' : undefined}
            aria-live={announce ? 'polite' : undefined}
          >
            {terminal ? (
              <AssistantShell data-testid="orbit-turn-answer">{assistantBody}</AssistantShell>
            ) : (
              assistantBody
            )}
            {terminal && time !== null ? (
              <AssistantMeta dateTime={createdAt} data-testid="orbit-turn-meta">
                {t(`${TOrbit}.thread.meta`, { who: t(`${TOrbit}.thread.orbit`), time })}
              </AssistantMeta>
            ) : null}
            {terminal && showFollowUps ? <OrbitFollowUps /> : null}
          </ReplyBody>
        </MessageRow>
      ) : null}
    </Root>
  );
};

export default React.memo(OrbitTurnCard);
